import { Check as CheckIcon, Sort as SortIcon } from '@mui/icons-material';
import { Box, Button, IconButton, Menu, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme } from '@mui/material';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { TruckShiftEntity } from 'src/entities/TruckShift.entity';
import { usePersistedJSON } from 'src/hooks/usePersistedJSON';
import { GridSelection } from 'src/pages/TruckShifts/bulkSelection';
import { CompanyGroupPresenter } from 'src/pages/TruckShifts/presenters/CompanyGroup.presenter';
import { HeaderCellsPresenter } from 'src/pages/TruckShifts/presenters/HeaderCells.presenter';
import {
  DEFAULT_TRUCK_SORT,
  groupByPartnerCompany,
  isTruckSortKey,
  ShiftCell,
  TRUCK_SORT_STORAGE_KEY,
  TruckSortKey,
} from 'src/pages/TruckShifts/truckShiftGrid';

const TRUCK_SORT_OPTIONS: { key: TruckSortKey; label: string }[] = [
  { key: 'licensePlate-asc', label: '車両名 昇順' },
  { key: 'licensePlate-desc', label: '車両名 降順' },
  { key: 'id-asc', label: '作成日 昇順' },
  { key: 'id-desc', label: '作成日 降順' },
];

type Props = {
  trucks: TruckShiftEntity[];
  dateColumns: string[];
  selection?: GridSelection;
  // CompanyGroupPresenter/BodyRowPresenter には selection オブジェクトではなく、実体の Set をそのまま渡す。
  // Set 自体は選択操作で変化しない限り同一参照が保たれるため、memo 化した配下コンポーネントの
  // 不要な全再レンダーを避けられる（selection オブジェクトは selectedTruckIds/selectedDates の
  // どちらかが変わるたびに新しい参照になってしまうため、そのままでは渡せない）。
  selectedTruckIds?: Set<number>;
  selectedDates?: Set<string>;
  onCellClick?: (truckId: number, date: string, cells: ShiftCell[], anchorEl: HTMLElement) => void;
};

export const TablePresenter: FC<Props> = ({ trucks, dateColumns, selection, selectedTruckIds, selectedDates, onCellClick }) => {
  const theme = useTheme();
  const [sortKey, setSortKey] = usePersistedJSON<TruckSortKey>(TRUCK_SORT_STORAGE_KEY, DEFAULT_TRUCK_SORT, isTruckSortKey);
  const groups = useMemo(() => groupByPartnerCompany(trucks, sortKey), [trucks, sortKey]);
  const [sortAnchor, setSortAnchor] = useState<null | HTMLElement>(null);
  const [collapsed, setCollapsed] = useState<Set<number | null>>(new Set());

  const toggle = useCallback((companyId: number | null) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId); else next.add(companyId);
      return next;
    });
  }, []);

  // CompanyGroupPresenter(memo) に渡す onToggle は companyId ごとに参照を固定する。
  // toggle は useCallback で安定しているが、`() => toggle(group.companyId)` を groups.map 内で
  // 都度生成すると呼ぶたびに新しい関数になり、対象外のグループまで memo bail-out に失敗して
  // 全行が再レンダーされてしまう（C3 の目的=トグルした行だけ再レンダーが未達になる）。
  const toggleHandlersRef = useRef(new Map<number | null, () => void>());
  const getToggleHandler = (companyId: number | null): (() => void) => {
    const cache = toggleHandlersRef.current;
    let handler = cache.get(companyId);
    if (!handler) {
      handler = () => toggle(companyId);
      cache.set(companyId, handler);
    }
    return handler;
  };

  // 削除済み（active=false）トラックは一括選択の対象外（CompanyGroupPresenter と同じ不変条件）。
  const allIds = trucks.filter((t) => t.active !== false).map((t) => t.id);
  const selectedCount = selection ? allIds.filter((id) => selection.isTruckSelected(id)).length : 0;
  const allSelected = selectedCount > 0 && selectedCount === allIds.length;

  return (
    <TableContainer component={Paper} sx={{ maxHeight: `calc(100vh - ${theme.spacing(20)})`, overflowX: 'auto' }}>
      <Table stickyHeader size="small" sx={{ width: 'auto' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 280, minWidth: 280, py: 0.75, px: 1.5, fontSize: 12, fontWeight: 700, position: 'sticky', left: 0, zIndex: 3, backgroundColor: 'background.paper', borderRight: '1px solid', borderColor: 'divider' }}>
              <Box display="flex" alignItems="center" gap={1}>
                車両（{trucks.length}台）
                {selection && (
                  <Button
                    size="small"
                    onClick={() => selection.setTrucksSelected(allIds, !allSelected)}
                    sx={{ textTransform: 'none', minWidth: 0, px: 1, py: 0, fontSize: 12 }}
                  >
                    {allSelected ? '全解除' : '全選択'}
                  </Button>
                )}
                <IconButton
                  size="small"
                  aria-label="車両の並び替え"
                  onClick={(e) => setSortAnchor(e.currentTarget)}
                  sx={{ ml: 'auto' }}
                >
                  <SortIcon fontSize="small" />
                </IconButton>
                <Menu
                  anchorEl={sortAnchor}
                  open={Boolean(sortAnchor)}
                  onClose={() => setSortAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  MenuListProps={{ sx: { p: 0 } }}
                >
                  {TRUCK_SORT_OPTIONS.map((option) => (
                    <MenuItem
                      key={option.key}
                      selected={sortKey === option.key}
                      onClick={() => { setSortKey(option.key); setSortAnchor(null); }}
                      sx={{ fontSize: 13, gap: 1 }}
                    >
                      <CheckIcon fontSize="small" sx={{ visibility: sortKey === option.key ? 'visible' : 'hidden' }} />
                      {option.label}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            </TableCell>
            <HeaderCellsPresenter dateColumns={dateColumns} selection={selection} />
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group) => (
            <CompanyGroupPresenter
              key={String(group.companyId)}
              group={group}
              dateColumns={dateColumns}
              expanded={!collapsed.has(group.companyId)}
              onToggle={getToggleHandler(group.companyId)}
              showCheckbox={selection != null}
              selectedTruckIds={selectedTruckIds}
              selectedDates={selectedDates}
              onToggleTruck={selection?.toggleTruck}
              onSetTrucksSelected={selection?.setTrucksSelected}
              onCellClick={onCellClick}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
